import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Ticket,
  Download,
  Printer,
  Calendar,
  MapPin,
  ArrowLeft,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { getCurrencySymbol } from "../../lib/data/currencies";

interface TicketData {
  id: number;
  event: number;
  user: number;
  ticket_type: string;
  unique_code: string;
  price: string;
  currency: string;
}

interface ReceiptState {
  tickets: TicketData[];
  eventTitle: string;
  totalAmount: number;
  totalTickets: number;
  purchaseDate: string;
}

const Receipt: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const state = location.state as ReceiptState | null;

  useEffect(() => {
    if (!state || !state.tickets || state.tickets.length === 0) {
      toast.error("No ticket information found");
      navigate("/");
    }
  }, [state, navigate]);

  if (!state || !state.tickets || state.tickets.length === 0) {
    return null;
  }

  const handlePrint = () => {
    window.print();
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Ticket code copied to clipboard!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 print:bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 print:hidden"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#17265a] hover:text-[#0f1a3f] transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </motion.button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#17265a] mb-2">
                Purchase Confirmed!
              </h1>
              <p className="text-gray-600">
                Your tickets have been successfully purchased
              </p>
            </div>
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePrint}
                className="px-4 py-2 bg-white border-2 border-[#17265a] text-[#17265a] rounded-lg hover:bg-[#17265a] hover:text-white transition-colors flex items-center gap-2"
              >
                <Printer className="w-5 h-5" />
                <span>Print</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Success Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 mb-8 text-white shadow-lg"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border-2 border-white/30">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">Payment Successful!</h2>
              <p className="text-white/90">
                Your tickets are ready. Show this receipt at the event.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Receipt Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-200"
        >
          <div className="border-b border-gray-200 pb-6 mb-6">
            <h3 className="text-2xl font-bold text-[#17265a] mb-4">
              Order Receipt
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 mb-1">Event</p>
                <p className="font-semibold text-gray-900">{state.eventTitle}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Purchase Date</p>
                <p className="font-semibold text-gray-900">
                  {formatDate(state.purchaseDate)}
                </p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Total Tickets</p>
                <p className="font-semibold text-gray-900">{state.totalTickets}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Total Amount</p>
                <p className="font-semibold text-[#17265a] text-lg">
                  {state.tickets.length > 0 && state.tickets[0].currency
                    ? getCurrencySymbol(state.tickets[0].currency)
                    : "$"}{state.totalAmount.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Tickets List */}
          <div>
            <h4 className="text-lg font-semibold text-[#17265a] mb-4 flex items-center gap-2">
              <Ticket className="w-5 h-5" />
              Your Tickets ({state.tickets.length})
            </h4>
            <div className="space-y-4">
              {state.tickets.map((ticket, index) => (
                <motion.div
                  key={ticket.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="bg-gradient-to-br from-[#17265a] to-[#1f2f6b] rounded-xl p-6 text-white border-2 border-[#17265a]/20"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Ticket className="w-5 h-5" />
                        <h5 className="text-xl font-bold">{ticket.ticket_type}</h5>
                      </div>
                      <p className="text-white/80 text-sm mb-4">
                        Ticket #{ticket.id}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/80 text-sm mb-1">Price</p>
                      <p className="text-2xl font-bold">
                        {getCurrencySymbol(ticket.currency)}{parseFloat(ticket.price).toFixed(2)}
                      </p>
                      <p className="text-white/60 text-xs">{ticket.currency}</p>
                    </div>
                  </div>

                  <div className="border-t border-white/20 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-xs mb-1">Unique Code</p>
                        <p className="font-mono text-lg font-semibold">
                          {ticket.unique_code}
                        </p>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleCopyCode(ticket.unique_code)}
                        className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-2 backdrop-blur-sm border border-white/20"
                      >
                        {copiedCode === ticket.unique_code ? (
                          <>
                            <Check className="w-4 h-4" />
                            <span className="text-sm">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            <span className="text-sm">Copy</span>
                          </>
                        )}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Important Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8"
        >
          <h4 className="text-lg font-semibold text-[#17265a] mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-blue-600" />
            Important Information
          </h4>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>
                Please save this receipt and bring it to the event. You can print it or show it on your mobile device.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>
                Each ticket has a unique code. Keep these codes safe and do not share them publicly.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>
                You should receive a confirmation email shortly with all ticket details.
              </span>
            </li>
          </ul>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4 print:hidden"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/")}
            className="flex-1 px-6 py-3 bg-[#17265a] text-white rounded-lg hover:bg-[#0f1a3f] transition-colors flex items-center justify-center gap-2 shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handlePrint}
            className="flex-1 px-6 py-3 bg-white border-2 border-[#17265a] text-[#17265a] rounded-lg hover:bg-[#17265a] hover:text-white transition-colors flex items-center justify-center gap-2 shadow-lg"
          >
            <Printer className="w-5 h-5" />
            <span>Print Receipt</span>
          </motion.button>
        </motion.div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white;
          }
          .print\\:hidden {
            display: none !important;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
    </div>
  );
};

export default Receipt;

